import React, { useEffect, useState } from "react";
import axios from "axios";
// import { useQuery } from "@tanstack/react-query";
// import { stagingGetRequest } from "../../api/authClient";
import StudentDistributionSummaryCards from "../../Components/studentDistribution/StudentDistributionSummaryCards";
import PresenceChartWithToggle from "../../Components/studentDistribution/PresenceChartWithToggle";
import FilterBar from "../../Components/studentDistribution/FilterBar";
import { DUMMY_LOCATIONS } from "../../services/dummyDistributionData";
import { getToken } from "../../services/storageService";
import { resolveGatewayBaseUrl } from "../../runtime/loadSharedDashboardConfig";

/**
 * Student Distribution page – summary cards use GET /distribution/summary-cards (cookies) then
 * WS /analytic/distribution-summary-cards (no token in query). Aggregate chart APIs optional via
 * PresenceChartWithToggle `apisEnabled`.
 * TODO: Using dummy locations – uncomment locations API when backend is ready.
 */
export const StudentDistribution: React.FC = () => {
  const getLocalDateString = () => {
    const d = new Date();
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const [selectedLocation, setSelectedLocation] = useState<string>("");
  const [selectedDate, setSelectedDate] = useState<string>(getLocalDateString());
  const [selectedDistribution, setSelectedDistribution] = useState<string>("student");
  const [distributionOptions, setDistributionOptions] = useState<Array<{ label: string; value: string }>>([
    { label: "Student", value: "student" },
    { label: "Staff", value: "staff" },
  ]);
  const roleFilter: "student" | "staff" =
    selectedDistribution === "student" ? "student" : "staff";

  const toDistributionLabel = (value: string) =>
    value
      .split("_")
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(" ");

  // TODO: Uncomment when locations API is ready
  // const [clientId, setClientId] = useState<string>("");
  // useEffect(() => {
  //   const fromStorage =
  //     localStorage.getItem("auth_user_id") ?? localStorage.getItem("clientId");
  //   if (fromStorage) {
  //     setClientId(fromStorage);
  //     return;
  //   }
  //   try {
  //     const authUser = JSON.parse(localStorage.getItem("auth_user") ?? "{}");
  //     if (authUser?.id) setClientId(authUser.id);
  //   } catch {
  //     // ignore
  //   }
  // }, []);
  // const { data: locationsRes } = useQuery({
  //   queryKey: ["locations", "student-distribution", clientId],
  //   queryFn: () =>
  //     stagingGetRequest("/location/search", {
  //       q: "",
  //       clientId,
  //       page: 1,
  //       pageSize: 100,
  //     }),
  //   enabled: !!clientId,
  // });
  // const locations = (() => {
  //   const res = locationsRes as Record<string, unknown> | undefined;
  //   const raw =
  //     (res?.data as { items?: unknown[] })?.items ??
  //     (res?.data as unknown[]) ??
  //     (res?.items as unknown[]) ??
  //     (res?.results as unknown[]) ??
  //     (res?.response as { locations?: unknown[] })?.locations ??
  //     (res?.locations as unknown[]) ??
  //     [];
  //   const list = Array.isArray(raw) ? raw : [];
  //   return list.map((loc: unknown) => {
  //     const l = loc as Record<string, unknown>;
  //     return {
  //       id: String(l.id ?? l._id ?? l.value ?? ""),
  //       name: String(l.name ?? l.label ?? l.area ?? l.building ?? l.id ?? l._id ?? "—"),
  //     };
  //   });
  // })();

  const locations = DUMMY_LOCATIONS;

  useEffect(() => {
    let mounted = true;

    const fetchPersonTypes = async () => {
      try {
        const token = getToken();
        const baseURL = resolveGatewayBaseUrl();
        const url = `${baseURL.replace(/\/$/, "")}/distribution/person-type`;

        const res = await axios.get<{ data?: string[] }>(url, {
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        });

        const personTypes = Array.isArray(res.data?.data) ? res.data.data : [];
        const nextOptions = personTypes.map((value) => ({
          value,
          label: toDistributionLabel(value),
        }));

        if (mounted && nextOptions.length > 0) {
          setDistributionOptions(nextOptions);
          if (!nextOptions.some((opt) => opt.value === selectedDistribution)) {
            setSelectedDistribution(nextOptions[0].value);
          }
        }
      } catch {
        // Keep fallback options when API is unavailable.
      }
    };

    fetchPersonTypes();
    return () => {
      mounted = false;
    };
  }, []);

  const getLocalDateRange = () => {
    if (!selectedDate) return { from: undefined, to: undefined };
    const fromLocal = new Date(selectedDate + "T00:00:00");
    const toLocal = new Date(selectedDate + "T23:59:59.999");
    return {
      from: fromLocal.toISOString(),
      to: toLocal.toISOString(),
    };
  };
  const { from, to } = getLocalDateRange();
  const filterParams = {
    location_id: selectedLocation || undefined,
    from,
    to,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  };

  return (
    <div className="min-h-screen w-full px-3 py-4 text-slate-800 sm:px-4 sm:py-5 lg:px-6 dark:text-slate-200">
      <div className="mx-auto w-full max-w-[1600px]">
      <div className="mb-4 sm:mb-5">
        <div className="flex items-start gap-2.5 sm:gap-3">
          <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-blue-100 text-blue-600 dark:bg-blue-600/20 dark:text-blue-400 sm:h-10 sm:w-10">
            <svg
              className="h-4 w-4 sm:h-5 sm:w-5"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M3 17l6-6 4 4 7-7" />
              <path d="M14 7h6v6" />
            </svg>
          </div>
          <div>
            <h1 className="text-lg font-bold leading-tight tracking-tight text-slate-900 dark:text-white sm:text-2xl">
              Presence & dwell time across locations
            </h1>
            <p className="mt-1 text-xs text-slate-600 dark:text-slate-400 sm:text-sm">
              Monitor {toDistributionLabel(selectedDistribution).toLowerCase()} presence and dwell
              time across locations
            </p>
          </div>
        </div>
      </div>

      <FilterBar
        filters={[
          {
            label: "Distribution",
            value: selectedDistribution,
            onChange: (val) => setSelectedDistribution(val),
            options: distributionOptions,
            hideEmptyOption: true,
          },
          {
            label: "Location",
            value: selectedLocation,
            onChange: (val) => setSelectedLocation(val),
            options: [
              { label: "All Locations", value: "" },
              ...locations.map((loc: { id: string; name: string }) => ({
                label: loc.name,
                value: loc.id,
              })),
            ],
            hideEmptyOption: true,
          },
          {
            label: "Date",
            value: selectedDate,
            onChange: setSelectedDate,
            type: "date",
          },
        ]}
        className="mb-3 sm:mb-4"
      />

      <StudentDistributionSummaryCards
        filterParams={filterParams}
        distributionType={selectedDistribution}
        roleFilter={roleFilter}
      />

      <div className="mt-3 sm:mt-4">
        <PresenceChartWithToggle
          {...({
            filterParams,
            roleFilter,
            distributionType: selectedDistribution,
            apisEnabled: true,
          } as any)}
        />
      </div>
      </div>
    </div>
  );
};

export default StudentDistribution;
